import React, { useState } from "react";
import Setting from "../../../assets/Setting.png";
import RestoreIcon from "@mui/icons-material/Restore";
import { ArrowUpward, ArrowDownward, Sync } from "@mui/icons-material";
import { ArrowDropUp, ArrowDropDown, UnfoldMore } from "@mui/icons-material";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  IconButton,
  TableRow,
  TextField,
  Menu,
  MenuItem,
  Checkbox,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";

const PurchaseByProducts = () => {
  const columnWidth = "10%";

  // State
  const [anchorEl, setAnchorEl] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [columns, setColumns] = useState({
    SKU: true,
    Product: true,
    Invoice: true,
    SupplierSKU: true,
    Date: true,
    Quantity: true,
    Price: true,
    Action: true,
  });

  const open = Boolean(anchorEl);

  // Placeholder purchase data
  const purchases = []; // You can populate this with actual data

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCheckboxChange = (column) => {
    setColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  const handleReset = () => {
    setColumns({
      SKU: true,
      Product: true,
      Invoice: true,
      SupplierSKU: true,
      Date: true,
      Quantity: true,
      Price: true,
      Action: true,
    });
    handleClose();
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderSortableHeader = (label, key) => {
    const isSorted = sortConfig.key === key;
    const direction = isSorted ? sortConfig.direction : null;

    return (
      <Box
        onClick={() => handleSort(key)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          color: "black",
          fontSize: "12px",
        }}
      >
        <b>{label}</b>
        {direction === "asc" && <ArrowDropUp sx={{ fontSize: 25 }} />}
        {direction === "desc" && <ArrowDropDown sx={{ fontSize: 25 }} />}
        {!direction && <UnfoldMore sx={{ fontSize: 25 }} />}
      </Box>
    );
  };

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(purchases.length / rowsPerPage));
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedPurchases = purchases.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleGoToPage = () => {
    const newPage = Number(page);
    if (!isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    } else {
      setPage(1); // Reset to page 1 if invalid input
    }
  };

  const handlePageInputChange = (e) => {
    const value = e.target.value;
    if (value === "" || Number(value) >= 1) {
      setPage(value);
    }
  };

  return (
    <Box>
      {/* Top Search */}
      <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
        <TextField
          label="Type to search"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: { xs: "100%", sm: "200px", md: "250px" },
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B5C4F1",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B5C4F1",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B5C4F1",
              },
            },
          }}
        />
        <Button
          variant="contained"
          sx={{
            borderRadius: "10px",
            backgroundColor: "#B5C4F1",
            color: "black",
            height: "40px",
            alignSelf: "center",
            whiteSpace: "nowrap",
          }}
        >
          Search
        </Button>
      </Box>

      {/* Table */}
      <TableContainer sx={{ borderRadius: 2, border: "1px solid black", overflow: "auto", mb: 3 }}>
        <Table>
          <TableHead sx={{ borderBottom: "1px solid black" }}>
            <TableRow sx={{ backgroundColor: "white", borderBottom: "1px solid black" }}>
              <TableCell sx={{ width: columnWidth, padding: "4px 6px", borderBottom: "1px solid black" }}>
                <img
                  src={Setting}
                  alt="setting"
                  style={{ filter: "invert()", width: "30px", cursor: "pointer" }}
                  onClick={handleClick}
                />
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  PaperProps={{
                    sx: {
                      width: 300,
                      maxHeight: 300,
                      borderRadius: "10px",
                      boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                      py: 0.5,
                    },
                  }}
                >
                  <MenuItem onClick={handleReset}>
                    <ListItemIcon>
                      <RestoreIcon fontSize="small" sx={{ color: "black" }} />
                    </ListItemIcon>
                    <ListItemText primary="Reset Layout" />
                  </MenuItem>
                  <Divider />
                  {["SKU", "Product"].map((col) => (
                    <MenuItem key={col} onClick={() => handleCheckboxChange(col)}>
                      <Checkbox checked={columns[col]} />
                      <ListItemText primary={col} />
                    </MenuItem>
                  ))}
                </Menu>
              </TableCell>

              {columns.SKU && <TableCell sx={{ borderBottom: "1px solid black" }}>{renderSortableHeader("SKU", "sku")}</TableCell>}
              {columns.Product && <TableCell sx={{ borderBottom: "1px solid black" }}>{renderSortableHeader("Product", "product")}</TableCell>}
              {columns.Invoice && <TableCell sx={{ borderBottom: "1px solid black" }}>{renderSortableHeader("Invoice#", "invoice")}</TableCell>}
              {columns.SupplierSKU && <TableCell sx={{ borderBottom: "1px solid black" }}>{renderSortableHeader("Supplier SKU", "supplierSku")}</TableCell>}
              {columns.Date && <TableCell sx={{ borderBottom: "1px solid black" }}><b>Date</b></TableCell>}
              {columns.Quantity && <TableCell sx={{ borderBottom: "1px solid black" }}>{renderSortableHeader("Quantity", "quantity")}</TableCell>}
              {columns.Price && <TableCell sx={{ borderBottom: "1px solid black" }}><b>Price</b></TableCell>}
              {columns.Action && <TableCell sx={{ borderBottom: "1px solid black" }}><b>Action</b></TableCell>}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedPurchases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={20} sx={{ textAlign: "center", py: 2, borderBottom: "1px solid black" }}>
                  No Purchase Found
                </TableCell>
              </TableRow>
            ) : (
              paginatedPurchases.map((purchase, index) => (
                <TableRow key={index}>
                  {columns.SKU && <TableCell>{purchase.sku}</TableCell>}
                  {columns.Product && <TableCell>{purchase.product}</TableCell>}
                  {columns.Invoice && <TableCell>{purchase.invoice}</TableCell>}
                  {columns.SupplierSKU && <TableCell>{purchase.supplierSku}</TableCell>}
                  {columns.Date && <TableCell>{purchase.date}</TableCell>}
                  {columns.Quantity && <TableCell>{purchase.quantity}</TableCell>}
                  {columns.Price && <TableCell>{purchase.price}</TableCell>}
                  {columns.Action && <TableCell>{purchase.action}</TableCell>}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <Box sx={{ display: "flex", gap: 1, p: 2 }}>
          <Button sx={{ fontSize: "12px", color: "#7C91E1", fontWeight: "bold", textTransform: "none" }} startIcon={<Sync />}>
            {purchases.length ? "Sync Data" : "No data to display"}
          </Button>
        </Box>
      </TableContainer>

      {/* Pagination */}
      <Box
        sx={{
          border: "1px solid black",
          borderRadius: "8px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 16px",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            sx={{
              backgroundColor: page === 1 ? "#e0e0e0" : "#d6dcf8",
              color: "white",
              borderRadius: "50%",
              width: 20,
              height: 20,
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                backgroundColor: page === 1 ? "#e0e0e0" : "#c0c9f5",
              },
            }}
          >
            <ArrowBackIosNew sx={{ fontSize: 12 }} />
          </IconButton>

          <Box
            sx={{
              border: "1px solid black",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
            }}
          >
            {page}
          </Box>

          <IconButton
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            sx={{
              backgroundColor: page === totalPages ? "#e0e0e0" : "#d6dcf8",
              color: "white",
              borderRadius: "50%",
              width: 20,
              height: 20,
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                backgroundColor: page === totalPages ? "#e0e0e0" : "#c0c9f5",
              },
            }}
          >
            <ArrowForwardIos sx={{ fontSize: 12 }} />
          </IconButton>

          <Button
            startIcon={<Sync />}
            sx={{
              fontSize: "12px",
              color: "#7C91E1",
              fontWeight: "bold",
              textTransform: "none",
            }}
          >
            Displaying purchases {purchases.length ? `${startIndex + 1} - ${Math.min(endIndex, purchases.length)} of ${purchases.length}` : "0"}
          </Button>

          <Box sx={{ fontWeight: "bold", fontSize: "12px", color: "#7C91E1" }}>
            Page {page} of {totalPages}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid black",
              borderRadius: "4px",
              padding: "2px 8px",
              gap: 1,
              height: 36,
            }}
          >
            <Box sx={{ fontSize: "14px", whiteSpace: "nowrap" }}>Show :</Box>
            <TextField
              select
              size="small"
              variant="standard"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(1); // Reset to first page when rows per page changes
              }}
              sx={{
                width: 60,
                "& .MuiInputBase-root": {
                  paddingTop: "4px",
                },
                "& .MuiSelect-select": {
                  paddingTop: "4px",
                  paddingBottom: "4px",
                },
                "& fieldset": {
                  border: "none",
                },
              }}
            >
              {[10, 20, 30].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ fontSize: "14px" }}>Go to page</Box>
          <TextField
            size="small"
            type="number"
            value={page}
            onChange={handlePageInputChange}
            inputProps={{ min: 1 }}
            sx={{ width: 60 }}
          />
          <Button
            variant="text"
            onClick={handleGoToPage}
            sx={{ fontSize: "14px", color: "#7C91E1", textTransform: "none" }}
          >
            GO
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PurchaseByProducts;